// One layout for every department page: Claude's take → This week → Plan → Records → Files.
import { useState } from "react";
import { CalendarClock, Compass, FileText, Folder, Target } from "lucide-react";
import { ago, api, Drawer, Empty, fmtDate, Pill, useLoad, useMe } from "./lib";

export type Area = "marketing" | "product" | "partnerships" | "pr";
// Each team's strategies on the Strategy page.
const STRATEGY_LINKS: Partial<Record<Area, [string, string][]>> = {
  marketing: [["gtm", "Go-to-market"], ["social_ads", "Social & paid ads"], ["seo_geo", "SEO & GEO"]],
  pr: [["pr", "PR"], ["speaking", "Speaking & awards"]],
};

export function DeptPage({ area, take, toolbar, children }: { area: Area; take?: React.ReactNode; toolbar?: React.ReactNode; children: React.ReactNode }) {
  const { data, reload } = useLoad<any>(`/api/dept/${area}`);
  const [editing, setEditing] = useState(false);
  const strategies = STRATEGY_LINKS[area];
  return (
    <div className="page">
      {take}
      {strategies && (
        <div className="row wrap strat-links">
          <span className="small muted"><Compass size={15} style={{ verticalAlign: -2 }} /> Our strategy:</span>
          {strategies.map(([key, label]) => <a key={key} className="pill accent" href={`#/strategy?s=${key}`}>{label}</a>)}
        </div>
      )}
      <div className="dept-grid">
        <ThisWeek items={data?.week} />
        <PlanCard plan={data?.plan} canEdit={!!data?.can_edit} onEdit={() => setEditing(true)} />
      </div>
      <section className="stack" aria-label="Records">
        <h2 className="dept-h">Records</h2>
        {toolbar}
        {children}
      </section>
      <FilesCard files={data?.plan?.files} canEdit={!!data?.can_edit} onEdit={() => setEditing(true)} />
      {editing && data && <PlanForm area={area} plan={data.plan} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), reload())} />}
    </div>
  );
}

function ThisWeek({ items }: { items?: any[] }) {
  return (
    <section className="card stack">
      <div className="row" style={{ gap: 8 }}><CalendarClock size={18} className="muted" /><h2>This week</h2></div>
      {!items ? <span className="muted small">Loading…</span> : items.length ? (
        <div className="list">
          {items.map((x, i) => (
            <a key={i} className="item clickable" href={x.href} style={{ textDecoration: "none", color: "inherit" }}>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="title" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.title}</div>
                <div className="tiny muted">{x.kind}{x.who ? ` · ${x.who}` : ""}</div>
              </div>
              <Pill tone={x.overdue ? "bad" : ""}>{x.overdue ? `Overdue · ${fmtDate(x.date)}` : fmtDate(x.date)}</Pill>
            </a>
          ))}
        </div>
      ) : <Empty>Nothing dated in the next 7 days.</Empty>}
    </section>
  );
}

function PlanCard({ plan, canEdit, onEdit }: { plan?: any; canEdit: boolean; onEdit: () => void }) {
  const empty = plan && !plan.goal && !plan.text && !plan.links.length;
  return (
    <section className="card stack">
      <div className="row between">
        <div className="row" style={{ gap: 8 }}><Target size={18} className="muted" /><h2>Plan</h2></div>
        {canEdit && <button className="btn sm" onClick={onEdit}>{empty ? "Add the plan" : "Edit"}</button>}
      </div>
      {!plan ? <span className="muted small">Loading…</span> : empty ? (
        <span className="muted small">No plan written down yet. Add the goal and a link to the plan doc, so everyone works from the same one.</span>
      ) : (
        <>
          {plan.goal && <div style={{ fontSize: 15, fontWeight: 600 }}>{plan.goal}</div>}
          {plan.text && <div className="small" style={{ whiteSpace: "pre-wrap" }}>{plan.text}</div>}
          {plan.links.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              {plan.links.map((l: any, i: number) => <a key={i} className="btn sm" href={l.href} target="_blank" rel="noreferrer"><FileText size={14} /> {l.label}</a>)}
            </div>
          )}
          {plan.updated_at && <div className="tiny muted">Updated {ago(plan.updated_at)}{plan.updated_by ? ` by ${plan.updated_by}` : ""}</div>}
        </>
      )}
    </section>
  );
}

function FilesCard({ files, canEdit, onEdit }: { files?: any[]; canEdit: boolean; onEdit: () => void }) {
  return (
    <section className="card stack">
      <div className="row between">
        <div className="row" style={{ gap: 8 }}><Folder size={18} className="muted" /><h2>Files</h2></div>
        {canEdit && <button className="btn sm" onClick={onEdit}>Add a link</button>}
      </div>
      {!files ? <span className="muted small">Loading…</span> : files.length ? (
        <div className="files-grid">
          {files.map((f, i) => (
            <a key={i} className="file-link" href={f.href} target="_blank" rel="noreferrer">
              <FileText size={16} />
              <span>{f.label}</span>
            </a>
          ))}
        </div>
      ) : <span className="muted small">No files linked yet. Add the Drive folders, Claude docs and boards this team uses.</span>}
    </section>
  );
}

export const toLines = (links: any[]) => links.filter((l) => !l.auto).map((l) => `${l.label} | ${l.href}`).join("\n");
export const fromLines = (text: string) =>
  text.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
    const i = line.lastIndexOf("|");
    return i < 0 ? { label: line, href: line } : { label: line.slice(0, i).trim(), href: line.slice(i + 1).trim() };
  });

function PlanForm({ area, plan, onClose, onSaved }: { area: Area; plan: any; onClose: () => void; onSaved: () => void }) {
  const { toast } = useMe();
  return (
    <Drawer title="Plan and files" sub={<span className="muted small">Everyone who can see this page sees these.</span>} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          try {
            await api(`/api/dept/${area}`, { method: "PUT", body: { goal: fd.get("goal"), text: fd.get("text"), links: fromLines(String(fd.get("links"))), files: fromLines(String(fd.get("files"))) } });
            toast("Saved");
            onSaved();
          } catch (err: any) {
            toast(err.message, true);
          }
        }}
      >
        <label className="field">Goal<input className="input" name="goal" defaultValue={plan.goal} placeholder="What this team is working toward" /></label>
        <label className="field">The plan in a few lines<textarea className="input" name="text" defaultValue={plan.text} /></label>
        <label className="field">
          Plan docs
          <textarea className="input" name="links" defaultValue={toLines(plan.links)} placeholder="November campaign plan | https://…" />
        </label>
        <label className="field">
          Files
          <textarea className="input" name="files" defaultValue={toLines(plan.files)} placeholder="Marketing Drive folder | https://drive.google.com/…" />
        </label>
        <p className="muted small" style={{ margin: 0 }}>One per line: a name, a bar, then the https link. Figma files for this team show up on their own.</p>
        <div className="row"><button className="btn primary">Save</button></div>
      </form>
    </Drawer>
  );
}

// Claude's take for pages without one list to rank: this week, at risk, not now.
export function AdviceTake({ page, intro, ask, footer }: { page: string; intro: string; ask: string; footer?: (data: any) => React.ReactNode }) {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>(`/api/insights/${page}`);
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState(false);
  if (!data) return null;
  const run = async () => {
    setBusy(true);
    try {
      await api(`/api/insights/${page}`, { method: "POST" });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const ins = data.insight?.result;
  const label = (t: string, color = "var(--accent-ink)") => (
    <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color, marginBottom: 4 }}>{t}</div>
  );
  return (
    <section className="card stack ai-card">
      <div className="row between">
        <div>
          <span className="ai-spark" /><strong className="ai-mark">Claude's take</strong>
          {data.insight && <span className="muted tiny"> · {ago(data.insight.created_at)} · AI suggestion, check it</span>}
        </div>
        {data.can_refresh && data.connected && (
          <button className="btn sm" disabled={busy} onClick={run}>{busy ? "Thinking…" : data.insight ? "Refresh" : ask}</button>
        )}
      </div>
      {!data.connected ? (
        <span className="muted small">AI isn't connected yet.</span>
      ) : !ins ? (
        <span className="muted small">{intro}</span>
      ) : (
        <>
          <div style={{ fontSize: 15 }}>{ins.headline}</div>
          <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))" }}>
            <div>
              {label("This week")}
              {ins.this_week.length ? ins.this_week.map((x: any, i: number) => (
                <div key={i} style={{ padding: "8px 6px" }}>
                  <div className="row between" style={{ gap: 8 }}><span style={{ fontWeight: 600, fontSize: 14 }}>{x.title}</span><Pill>{x.who}</Pill></div>
                  <div className="small muted">{x.why}</div>
                  <div className="small" style={{ marginTop: 2 }}><span style={{ color: "var(--accent-ink)", fontWeight: 600 }}>Next: </span>{x.next_step}</div>
                </div>
              )) : <span className="muted small">Nothing urgent.</span>}
            </div>
            <div>
              {label("At risk", "var(--muted)")}
              {ins.at_risk.length ? ins.at_risk.map((x: any, i: number) => (
                <div key={i} style={{ padding: "8px 6px" }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{x.title}</div>
                  <div className="small muted">{x.why}</div>
                </div>
              )) : <span className="muted small">Nothing flagged.</span>}
            </div>
          </div>
          {ins.watch_out && <div className="banner warn small"><div><strong>Watch out: </strong>{ins.watch_out}</div></div>}
          {ins.not_now.length > 0 && (
            <div>
              <button className="btn ghost sm" style={{ padding: 0 }} onClick={() => setMore(!more)}>{more ? "Hide" : `Not now (${ins.not_now.length})`}</button>
              {more && ins.not_now.map((x: any, i: number) => (
                <div key={i} style={{ padding: "8px 6px" }}><div style={{ fontWeight: 600, fontSize: 14 }}>{x.title}</div><div className="small muted">{x.why}</div></div>
              ))}
            </div>
          )}
        </>
      )}
      {footer?.(data)}
    </section>
  );
}
